import {Component, OnInit, AfterViewInit} from '@angular/core';
import {BaseComponent} from '../../../base/base.component';
import {Router} from '@angular/router';
import {TranslateService} from '@ngx-translate/core';
import {
    DaycareChildPage,
    DaycareGuardian,
    GetUnitiesGQL,
    SearchDaycareChildGQL,
    SearchDaycareChildInput,
    SearchDaycareChildQuery,
    Unity,
    User
} from '../../../../generated/graphql';
import {QueryRef} from 'apollo-angular';
import {UserService} from '../../../service/user.service';
import {FilterService} from '../../../service/filter.service';
import {DaycareService} from '../../../service/daycare.service';

// Fatia 5B — tela de Gestão da Creche (listagem de crianças + responsáveis autorizados).
// Somente admin e local-manager acessam (§47.8). O back-end escopa por unidade:
// admin pode escolher a unidade; local-manager é FORÇADO à sua própria (ignora unityId),
// por isso o seletor de unidade NÃO aparece p/ ele (§47.5).
@Component({
    selector: 'app-daycare-home',
    templateUrl: './daycare-home.component.html',
    styleUrls: ['./daycare-home.component.scss']
})
export class DaycareHomeComponent extends BaseComponent implements OnInit, AfterViewInit {

    filterKey = 'daycare_filter';
    user = new User();
    searchQuery: QueryRef<SearchDaycareChildQuery>;
    page: DaycareChildPage = new DaycareChildPage();
    filter: SearchDaycareChildInput = new SearchDaycareChildInput();

    showUnitySelector = false; // só admin
    canWrite = false; // admin + local-manager
    unities: Array<Unity> = [];

    constructor(public router: Router,
                public translate: TranslateService,
                public userService: UserService,
                private filterService: FilterService,
                private daycareService: DaycareService,
                private searchDaycareChildGQL: SearchDaycareChildGQL,
                private getUnitiesGQL: GetUnitiesGQL
    ) {
        super(router, translate);
    }

    ngOnInit() {
        const savedFilter: SearchDaycareChildInput = this.filterService.getFilter(this.filterKey);
        if (savedFilter) {
            this.filter = {
                keyword: savedFilter.keyword || '',
                unityId: savedFilter.unityId || null,
                page: 1
            };
        }
    }

    ngAfterViewInit(): void {
        this.userService.user.subscribe({
            next: data => {
                this.user = data as User;
                this.actionUserProfileBTN();

                // Gate de tela: apenas admin/local-manager (§47.8).
                if (this.actionUserProfile() === false) {
                    super.showMessage(this.translate.instant('atencao'), this.translate.instant('semacesso'), 'warning');
                    this.router.navigate(['/']);
                    return;
                }

                if (this.showUnitySelector) {
                    this.loadUnities();
                }
                this.actionList();
            }, error: err => super.onError(err)
        });
    }

    loadUnities() {
        this.getUnitiesGQL.watch().valueChanges.subscribe(({data}) => {
            this.unities = (data.unities || []) as Array<Unity>;
        }, error => super.onError(error));
    }

    buildFilter(): SearchDaycareChildInput {
        return {
            keyword: this.filter.keyword || '',
            // local-manager: back ignora unityId e força a unidade dele; enviamos null.
            unityId: this.showUnitySelector ? (this.filter.unityId || null) : null,
            page: this.filter.page || 1
        };
    }

    actionList() {
        this.filter.page = 1;
        this.searchQuery = this.searchDaycareChildGQL.watch({data: this.buildFilter()});
        this.searchQuery.valueChanges.subscribe(({data}) => {
            this.page = data.searchDaycareChild as DaycareChildPage;
        }, error => super.onError(error));
    }

    actionFilter() {
        this.searchQuery.refetch({data: this.buildFilter()});
        this.filterService.setFilter(this.filterKey, this.filter);
    }

    actionSearch() {
        this.filter.page = 1;
        this.actionFilter();
    }

    pageChanged(event) {
        this.filter.page = event;
        this.actionFilter();
    }

    clearFilter() {
        this.filterService.clearFilter(this.filterKey);
        this.filter = {keyword: '', unityId: null, page: 1};
        this.actionFilter();
    }

    // Exporta o Excel canônico da unidade. admin PRECISA escolher a unidade
    // (o back exige unityId para admin); local-manager é forçado à dele.
    actionExcel() {
        if (this.showUnitySelector && !this.filter.unityId) {
            super.showMessage(this.translate.instant('atencao'), this.translate.instant('daycare.selecioneunidade'), 'warning');
            return;
        }
        const unityId = this.showUnitySelector ? this.filter.unityId : null;
        this.daycareService.exportExcel(unityId).subscribe({
            next: (data: any) => super.downloadHandlerFile(data),
            error: (err) => super.onError(err)
        });
    }

    // Rótulo de cada pessoa autorizada: nome + documento; sem documento, só o nome (nunca "null").
    guardianLabel(g: DaycareGuardian): string {
        if (g && g.documentRaw) {
            return `${g.name} — ${g.documentRaw}`;
        }
        return g ? g.name : '';
    }

    isParent(g: DaycareGuardian): boolean {
        return !!g && g.kind === 'parent';
    }

    // Acesso à tela de Gestão: somente admin e local-manager (§47.8).
    actionUserProfile(): boolean {
        switch (this.user.profile) {
            case 'admin':
                return true;
            case 'local-manager':
                return true;
            default:
                return false;
        }
    }

    actionUserProfileBTN() {
        // Seletor de unidade só p/ admin (cross-unity). local-manager é forçado à própria unidade.
        this.showUnitySelector = this.user.profile === 'admin';
        // Escrita (nova criança/detalhe): admin e local-manager (§47.2).
        this.canWrite = this.user.profile === 'admin' || this.user.profile === 'local-manager';
    }
}
